import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from './BrandLogo';
import {
  ShieldAlert,
  CheckCircle2,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Loader2,
  ShieldCheck,
  Activity,
  FileSpreadsheet
} from 'lucide-react';

const BG_IMAGE = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1600&q=80&auto=format&fit=crop";

function AlertMessage({ type, message }: { type: 'error' | 'success'; message: string }) {
  const isError = type === 'error';
  return (
    <div
      role="alert"
      className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-medium mb-4 transition-all duration-200 ${
        isError
          ? 'bg-rose-500/15 border border-rose-500/30 text-rose-200'
          : 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-200'
      }`}
    >
      {isError ? (
        <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0" />
      ) : (
        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
      )}
      <span>{message}</span>
    </div>
  );
}

interface InputFieldProps {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  icon?: React.ReactNode;
  rightAction?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
}

function InputField({ id, label, type, value, onChange, icon, rightAction, placeholder, required = true }: InputFieldProps) {
  const hasValue = Boolean(value && value.toString().trim().length > 0);

  return (
    <div className="relative mb-2.5 sm:mb-3 group">
      <div className="flex items-center justify-between mb-1">
        <label
          htmlFor={id}
          className={`block text-xs font-semibold tracking-wide transition-colors duration-200 ${
            hasValue ? 'text-primary-300 font-bold' : 'text-slate-200 group-focus-within:text-primary-300'
          }`}
        >
          {label}
        </label>
        {hasValue && (
          <span className="text-[10px] font-semibold text-emerald-400 flex items-center gap-1 animate-fade-in tracking-wider">
            ✓ FILLED
          </span>
        )}
      </div>
      <div className="relative flex items-center">
        {icon && (
          <div
            className={`absolute left-3 z-10 pointer-events-none flex items-center justify-center transition-all duration-200 ${
              hasValue
                ? 'text-cyan-400 scale-110 drop-shadow-[0_0_10px_rgba(34,211,238,0.7)]'
                : 'text-slate-300 group-focus-within:text-cyan-400 group-focus-within:scale-105'
            }`}
          >
            {icon}
          </div>
        )}
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className={`w-full bg-slate-900/90 border rounded-xl text-xs sm:text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all duration-200 backdrop-blur-md shadow-inner ${
            hasValue
              ? 'border-cyan-500/60 focus:border-cyan-400 focus:ring-cyan-500/30 ring-1 ring-cyan-500/30'
              : 'border-slate-700/90 focus:border-primary-400 focus:ring-primary-500/25'
          } ${
            icon ? 'pl-9' : 'pl-3'
          } ${rightAction ? 'pr-9' : 'pr-3'} py-2 sm:py-2.5`}
        />
        {rightAction && (
          <div className="absolute right-2 z-10 flex items-center">
            {rightAction}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();

  // Sign In state
  const [siEmail, setSiEmail] = useState('');
  const [siPass, setSiPass] = useState('');
  const [siLoading, setSiLoading] = useState(false);
  const [siError, setSiError] = useState('');
  const [siSuccess, setSiSuccess] = useState('');
  const [siShow, setSiShow] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSiError('');
    setSiSuccess('');
    setSiLoading(true);
    try {
      const ok = await login(siEmail, siPass);
      if (!ok) setSiError('Invalid email or password. Please verify your credentials.');
    } catch {
      setSiError('Unable to connect to authentication server. Please try again.');
    } finally {
      setSiLoading(false);
    }
  };

  return (
    <div className="h-screen w-full relative overflow-hidden bg-slate-950 flex flex-col justify-between text-slate-100 selection:bg-primary-500 selection:text-white">
      {/* Background Image Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center filter brightness-[0.32] blur-[2px] scale-105 transition-all duration-700 pointer-events-none"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/95 via-slate-900/85 to-blue-950/75 pointer-events-none" />

      {/* Decorative Glow Orb */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl pointer-events-none" />

      {/* Top Navigation Bar */}
      <header className="relative z-20 flex items-center justify-between px-6 sm:px-12 py-3 sm:py-4 shrink-0">
        <BrandLogo variant="full" size="md" theme="dark" />
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900/80 border border-slate-700/80 backdrop-blur-md shadow-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-semibold text-slate-300 tracking-wide">Enterprise Portal</span>
        </div>
      </header>

      {/* Main Authentication Card */}
      <main className="relative z-20 flex-1 flex items-center justify-center px-4 py-1 sm:py-2 min-h-0">
        <div className="w-full max-w-lg bg-slate-900/90 border border-slate-700/80 rounded-2xl p-5 sm:p-6 backdrop-blur-2xl shadow-2xl">
          {/* Header */}
          <div className="mb-3 sm:mb-4 text-center sm:text-left">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-300 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider mb-1.5 sm:mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-primary-400" />
              Secure Enterprise Access
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-white leading-tight">
              Sign in to ClaimsAI
            </h1>
            <p className="text-[11px] sm:text-xs text-slate-300 mt-0.5">
              Access your clinical analytics and fraud monitoring workspace.
            </p>
          </div>

          {siError && <AlertMessage type="error" message={siError} />}
          {siSuccess && <AlertMessage type="success" message={siSuccess} />}

          <form onSubmit={handleSignIn} className="space-y-1 sm:space-y-1.5">
            <InputField
              id="siEmail"
              label="Work Email"
              type="email"
              placeholder="name@organization.com"
              value={siEmail}
              onChange={(e) => setSiEmail(e.target.value)}
              icon={<Mail className="w-4 h-4" />}
            />
            <InputField
              id="siPass"
              label="Password"
              type={siShow ? 'text' : 'password'}
              placeholder="••••••••••••"
              value={siPass}
              onChange={(e) => setSiPass(e.target.value)}
              icon={<Lock className="w-4 h-4" />}
              rightAction={
                <button
                  type="button"
                  onClick={() => setSiShow(!siShow)}
                  className={`p-1 rounded-lg transition-colors cursor-pointer ${
                    siPass ? 'text-cyan-400 hover:text-cyan-300 hover:bg-slate-800' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                  tabIndex={-1}
                  title={siShow ? "Hide password" : "Show password"}
                >
                  {siShow ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              }
            />

            <div className="pt-2">
              <button
                type="submit"
                disabled={siLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-primary-600 hover:from-blue-500 hover:to-primary-500 text-white font-bold text-sm shadow-lg shadow-primary-600/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-[0.99]"
              >
                {siLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-white" />
                    <span>Verifying Credentials...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-4 h-4 text-white" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Quick Stats Trust Footer */}
          <div className="mt-3 sm:mt-4 pt-3 border-t border-slate-800/90 flex items-center justify-between text-[11px] text-slate-200">
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-primary-400" />
              <span>Multi-Layer AI Engine</span>
            </div>
            <div className="flex items-center gap-1.5">
              <FileSpreadsheet className="w-3.5 h-3.5 text-primary-400" />
              <span>FHIR / Claims Compliant</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-20 py-2 sm:py-3 px-6 text-center text-[11px] sm:text-xs text-slate-400 shrink-0">
        &copy; {new Date().getFullYear()} ClaimsAI Healthcare Analytics Platform. All rights reserved.
      </footer>
    </div>
  );
}